/**
 * Material Module Permission Selectors
 *
 * These selectors use the generic permissions utility to check
 * material-specific permissions in the current space.
 *
 * Usage in containers:
 *   import { canCreateMaterials, canEditMaterials } from '@core/materials/permissions';
 *
 *   const mapStateToProps = (state) => ({
 *     canCreate: canCreateMaterials(state),
 *     canEdit: canEditMaterials(state),
 *   });
 */

import { AulaState } from '../types/state';
import {
  isInstructor,
  accessingCurrentSpaceAsStaff,
} from '../selectors/classroom';
import {
  hasPermission,
  isRbacEnabled,
  MATERIAL_PERMISSIONS,
  getPermissionDeniedMessage as getGenericPermissionDeniedMessage,
  createPermissionDeniedToast as createGenericPermissionDeniedToast,
  evaluatePermission,
  PermissionEvaluationInput,
  PermissionEvaluationResult,
} from '../utils/permissions';

// Re-export permission keys for convenience
export { MATERIAL_PERMISSIONS } from '../utils/permissions';
export type { MaterialPermissionKey } from '../utils/permissions';

// =============================================================================
// Material Permission Selectors
// =============================================================================

/**
 * Check if user can create materials in the current space
 * Falls back to instructor check ONLY if RBAC is not enabled
 */
export const canCreateMaterials = (state: AulaState): boolean => {
  // If RBAC is enabled, strictly use permissions (no fallback)
  if (isRbacEnabled(state)) {
    return hasPermission(state, MATERIAL_PERMISSIONS.CREATE, false);
  }
  // Legacy: fall back to instructor check
  return isInstructor(state);
};

/**
 * Check if user can edit materials in the current space
 * Falls back to instructor check ONLY if RBAC is not enabled
 */
export const canEditMaterials = (state: AulaState): boolean => {
  // If RBAC is enabled, strictly use permissions (no fallback)
  if (isRbacEnabled(state)) {
    return hasPermission(state, MATERIAL_PERMISSIONS.EDIT, false);
  }
  // Legacy: fall back to instructor check
  return isInstructor(state);
};

/**
 * Check if user can delete materials in the current space
 * Falls back to instructor check ONLY if RBAC is not enabled
 */
export const canDeleteMaterials = (state: AulaState): boolean => {
  // If RBAC is enabled, strictly use permissions (no fallback)
  if (isRbacEnabled(state)) {
    return hasPermission(state, MATERIAL_PERMISSIONS.DELETE, false);
  }
  // Legacy: fall back to instructor check
  return isInstructor(state);
};

/**
 * Check if user can view materials in the current space
 * Defaults to true for all users when RBAC is not enabled
 */
export const canViewMaterials = (state: AulaState): boolean => {
  // If RBAC is enabled, strictly use permissions
  if (isRbacEnabled(state)) {
    return hasPermission(state, MATERIAL_PERMISSIONS.VIEW, false);
  }
  // Legacy: everyone can view
  return true;
};

/**
 * Check if user can view hidden materials
 * - Staff can always see hidden materials
 * - With RBAC: edit, create, publish, or educatorArea
 *   (matches backend includeHidden — view alone is not enough)
 * - Legacy: only instructors can see hidden materials
 *
 * Note: Seeing hidden materials != being able to unhide them (that requires publish permission)
 */
export const canViewHiddenMaterials = (state: AulaState): boolean => {
  // Staff can always view hidden materials
  if (accessingCurrentSpaceAsStaff(state)) {
    return true;
  }

  // If RBAC is enabled, check for permissions that allow viewing hidden materials
  if (isRbacEnabled(state)) {
    return (
      canEditMaterials(state) ||
      canCreateMaterials(state) ||
      hasPermission(state, MATERIAL_PERMISSIONS.PUBLISH, false) ||
      canAccessEducatorArea(state)
    );
  }

  // Legacy fallback: only instructors can see hidden materials
  return isInstructor(state);
};

/**
 * Check if user can access the educator area in the current space
 * - Staff can always access educator area
 * - With RBAC: checks for material:educatorArea permission
 * - Legacy: only instructors can access educator area
 */
export const canAccessEducatorArea = (state: AulaState): boolean => {
  // Staff can always access educator area
  if (accessingCurrentSpaceAsStaff(state)) {
    return true;
  }

  // If RBAC is enabled, check for educator area permission
  if (isRbacEnabled(state)) {
    return hasPermission(state, MATERIAL_PERMISSIONS.EDUCATOR_AREA, false);
  }

  // Legacy fallback: only instructors can access educator area
  return isInstructor(state);
};

// =============================================================================
// Permission Denied Messages & Toast
// =============================================================================

/**
 * Get user-friendly message for material permission denial
 */
export const getPermissionDeniedMessage = (permissionKey: string): string => {
  return getGenericPermissionDeniedMessage(permissionKey);
};

/**
 * Create a toast object for permission denial
 * Usage: dispatch(showToast(createPermissionDeniedToast(MATERIAL_PERMISSIONS.EDIT)))
 */
export const createPermissionDeniedToast = (permissionKey: string) => {
  return createGenericPermissionDeniedToast(permissionKey);
};

// =============================================================================
// Utility Selectors
// =============================================================================

/**
 * Check if the current space has RBAC enabled
 */
export const isMaterialRbacEnabled = (state: AulaState): boolean => {
  return isRbacEnabled(state);
};

/**
 * Get all material permissions object for the current user
 * Useful for passing to components that need multiple permission checks
 */
export const getMaterialPermissions = (state: AulaState) => ({
  canCreate: canCreateMaterials(state),
  canEdit: canEditMaterials(state),
  canDelete: canDeleteMaterials(state),
  canView: canViewMaterials(state),
  canViewHidden: canViewHiddenMaterials(state),
  canAccessEducatorArea: canAccessEducatorArea(state),
});

export type MaterialPermissions = ReturnType<typeof getMaterialPermissions>;

// =============================================================================
// Material action-to-permission mapping
// =============================================================================

export const MATERIAL_ACTIONS = {
  CREATE_PAGE: {
    permission: MATERIAL_PERMISSIONS.CREATE,
    label: 'create new pages or sections',
  },
  ADD_DISCUSSION: {
    permission: MATERIAL_PERMISSIONS.CREATE,
    label: 'add discussions to a material',
  },
  IMPORT_MATERIAL: {
    permission: MATERIAL_PERMISSIONS.CREATE,
    label: 'import materials',
  },
  IMPORT_PAGE: {
    permission: MATERIAL_PERMISSIONS.EDIT,
    label: 'import pages',
  },
  EXPORT_MATERIAL: {
    permission: MATERIAL_PERMISSIONS.EXPORT,
    label: 'export materials',
  },
  EXPORT_PAGE: {
    permission: MATERIAL_PERMISSIONS.EXPORT,
    label: 'export pages',
  },
  DELETE_SECTION: {
    permission: MATERIAL_PERMISSIONS.DELETE,
    label: 'delete sections',
  },
  DELETE_PAGE: {
    permission: MATERIAL_PERMISSIONS.DELETE,
    label: 'delete pages',
  },
  RESTORE_PAGE: {
    permission: MATERIAL_PERMISSIONS.RESTORE,
    label: 'restore deleted pages',
  },
  PUBLISH_PAGE: {
    permission: MATERIAL_PERMISSIONS.PUBLISH,
    label: 'publish pages',
  },
  PUBLISH_SECTION: {
    permission: MATERIAL_PERMISSIONS.PUBLISH,
    label: 'publish sections',
  },
  SCHEDULE_SECTION: {
    permission: MATERIAL_PERMISSIONS.PUBLISH,
    label: 'schedule sections',
  },
  SCHEDULE_PAGE: {
    permission: MATERIAL_PERMISSIONS.PUBLISH,
    label: 'schedule pages',
  },
  TOGGLE_VISIBILITY: {
    permission: MATERIAL_PERMISSIONS.PUBLISH,
    label: 'show or hide materials',
  },
  EDIT_PAGE: {
    permission: MATERIAL_PERMISSIONS.EDIT,
    label: 'edit pages',
  },
  RENAME_SECTION: {
    permission: MATERIAL_PERMISSIONS.EDIT,
    label: 'rename sections',
  },
  REORDER_MATERIAL: {
    permission: MATERIAL_PERMISSIONS.EDIT,
    label: 'reorder materials',
  },
  VIEW_MATERIAL: {
    permission: MATERIAL_PERMISSIONS.VIEW,
    label: 'view materials',
  },
  VIEW_EDUCATOR_CONVERSATION: {
    permission: MATERIAL_PERMISSIONS.VIEW,
    label: 'view educator conversations',
  },
  VIEW_HISTORY: {
    permission: MATERIAL_PERMISSIONS.HISTORY,
    label: "view a material's history",
  },
} as const;

export type MaterialAction = keyof typeof MATERIAL_ACTIONS;

type MaterialActionEvaluationInput = Omit<
  PermissionEvaluationInput,
  'permission' | 'actionLabel'
>;

export const evaluateMaterialAction = (
  action: MaterialAction,
  options: MaterialActionEvaluationInput = {}
): PermissionEvaluationResult => {
  const definition = MATERIAL_ACTIONS[action];
  if (!definition) {
    throw new Error(`Unknown material action: ${action}`);
  }

  return evaluatePermission({
    permission: definition.permission,
    actionLabel: definition.label,
    ...options,
  });
};

export const canPerformMaterialAction = (
  action: MaterialAction,
  options: MaterialActionEvaluationInput = {}
): boolean => evaluateMaterialAction(action, options).allowed;
